import { directories, uploadToS3 } from "../../../../utils/S3";

/** File picker `accept` — literal extensions only (not .jpeg / .jfif). */
export const REPORT_IMAGE_ACCEPT = ".jpg,.png";

/** MIME types paired with REPORT_IMAGE_ACCEPT (extension still required). */
export const REPORT_IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
];

export const REPORT_MAX_ATTACHMENTS = 4;
export const REPORT_DESCRIPTION_MAX_LENGTH = 300;
export const REPORT_COMMENT_MAX_LENGTH = 300;

/** Remaining slots before hitting REPORT_MAX_ATTACHMENTS. */
export const getReportAttachmentSlotsRemaining = (usedCount = 0) =>
  Math.max(0, REPORT_MAX_ATTACHMENTS - Number(usedCount || 0));

/** Pick upload helper text for current remaining capacity. */
export const getReportUploadHint = (labels, remaining) => {
  if (remaining <= 0) {
    return {
      text: labels?.uploadLimitReached || labels?.uploadHint || "",
      formatText: "",
      tone: "limit",
    };
  }
  if (
    remaining < REPORT_MAX_ATTACHMENTS &&
    labels?.uploadRemainingHint
  ) {
    return {
      text: String(labels.uploadRemainingHint).replace(
        "{count}",
        String(remaining),
      ),
      formatText: labels?.uploadFormatHint || "",
      tone: "remaining",
    };
  }
  return {
    text: labels?.uploadHint || "",
    formatText: "",
    tone: "default",
  };
};

/** True only for files whose name ends with .jpg or .png. */
export const isReportImageFile = (file) => {
  const name = String(file?.name || "").toLowerCase();
  return name.endsWith(".jpg") || name.endsWith(".png");
};

/** Keep valid image files only, capped at REPORT_MAX_ATTACHMENTS. */
export const normalizeReportImageFiles = (files = []) =>
  (Array.isArray(files) ? files : [files])
    .filter((file) => file && isReportImageFile(file))
    .slice(0, REPORT_MAX_ATTACHMENTS);

/** Upload an optional report attachment and return S3 URL (or null). */
export const uploadReportAttachment = async (file, options = {}) => {
  if (!file) return null;
  if (!isReportImageFile(file)) {
    throw new Error("Invalid report attachment type");
  }

  const ext = String(file.name).toLowerCase().endsWith(".png") ? "png" : "jpg";
  const fileName = `report.${ext}`;
  const data = await uploadToS3(file, fileName, directories.events, {
    onProgress: options.onProgress,
  });
  return data?.link || null;
};

/** Upload up to 4 attachments; `onProgress` receives overall 0–100. */
export const uploadReportAttachments = async (files = [], options = {}) => {
  const list = normalizeReportImageFiles(files);
  if (!list.length) return [];

  const urls = [];
  for (let i = 0; i < list.length; i += 1) {
    const url = await uploadReportAttachment(list[i], {
      onProgress: (percent) => {
        const overall = Math.round(((i + percent / 100) / list.length) * 100);
        options.onProgress?.(overall);
      },
    });
    if (url) urls.push(url);
  }
  return urls;
};

/** Build logistics_issue_images_attributes from uploaded URL(s). */
export const buildIssueImagesAttributes = (imageUrls = []) =>
  imageUrls
    .filter(Boolean)
    .slice(0, REPORT_MAX_ATTACHMENTS)
    .map((image_url) => ({ image_url }));
